import { createPrng } from '@play-co/replicant';
import { weightedRandom } from '../utils/random';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import {
  jankenMoves,
  jankenRewardTable,
  JankenMove,
  JankenResult,
} from './janken.ruleset';

/**
 * Deterministically derive the sender's move from the challenge and sender IDs.
 * Uses a seeded PRNG so the same inputs always produce the same move.
 *
 * @example
 * deriveJankenSenderMove('jnk_1708300000000_482917365', 'Uf4a3b2c1d0e9f8a7b6c5d4e3f2a1b0cd')
 * // => 'rock'
 */
export function deriveJankenSenderMove(
  challengeId: string,
  senderId: string,
): JankenMove {
  const random = createPrng(`${challengeId}-${senderId}`);
  return jankenMoves[Math.floor(random() * jankenMoves.length)];
}

/**
 * Deterministically derive the receiver's move.
 * Includes both senderId and receiverId in the seed to ensure the
 * receiver's PRNG sequence is distinct from the sender's.
 *
 * @example
 * deriveJankenReceiverMove('jnk_1708300000000_482917365', 'Uf4a3b2c1d0e9f8a7b6c5d4e3f2a1b0cd', 'U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b')
 * // => 'paper'
 */
export function deriveJankenReceiverMove(
  challengeId: string,
  senderId: string,
  receiverId: string,
): JankenMove {
  const random = createPrng(`${challengeId}-${senderId}-${receiverId}`);
  return jankenMoves[Math.floor(random() * jankenMoves.length)];
}

/**
 * Compare two janken moves. Returns the outcome for both sides.
 *
 * @example
 * compareJanken('scissors', 'rock')
 * // => { sender: 'lose', receiver: 'win' }   (receiver's rock beats sender's scissors)
 *
 * compareJanken('rock', 'scissors')
 * // => { sender: 'win', receiver: 'lose' }   (sender's rock beats receiver's scissors)
 *
 * compareJanken('paper', 'paper')
 * // => { sender: 'draw', receiver: 'draw' }
 */
export function compareJanken(
  senderMove: JankenMove,
  receiverMove: JankenMove,
): JankenResult {
  if (senderMove === receiverMove) {
    return { sender: 'draw', receiver: 'draw' };
  }

  const wins: Record<JankenMove, JankenMove> = {
    rock: 'scissors',
    paper: 'rock',
    scissors: 'paper',
  };

  if (wins[receiverMove] === senderMove) {
    return { sender: 'lose', receiver: 'win' };
  }
  return { sender: 'win', receiver: 'lose' };
}

/**
 * Derive both moves and the result for a janken challenge.
 *
 * @example
 * deriveJankenOutcome('jnk_1708300000000_482917365', 'Uf4a3b2c1d0e9f8a7b6c5d4e3f2a1b0cd', 'U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b')
 * // => {
 * //   senderMove: 'rock',
 * //   receiverMove: 'paper',
 * //   result: { sender: 'lose', receiver: 'win' },
 * // }
 */
export function deriveJankenOutcome(
  challengeId: string,
  senderId: string,
  receiverId: string,
): {
  senderMove: JankenMove;
  receiverMove: JankenMove;
  result: JankenResult;
} {
  const senderMove = deriveJankenSenderMove(challengeId, senderId);
  const receiverMove = deriveJankenReceiverMove(
    challengeId,
    senderId,
    receiverId,
  );
  const result = compareJanken(senderMove, receiverMove);
  return { senderMove, receiverMove, result };
}

/**
 * Extract the creation timestamp from a challengeId.
 * ChallengeId format: `jnk_${timestamp}_${random}`.
 * Returns `null` if the format is invalid or missing the `jnk` prefix.
 *
 * @example
 * parseJankenChallengeTimestamp('jnk_1708300000000_482917365')
 * // => 1708300000000
 *
 * parseJankenChallengeTimestamp('invalid')
 * // => null
 */
export function parseJankenChallengeTimestamp(
  challengeId: string,
): number | null {
  const parts = challengeId.split('_');
  if (parts.length !== 3 || parts[0] !== 'jnk' || parts[2].length === 0)
    return null;
  const ts = Number(parts[1]);
  return Number.isInteger(ts) && ts > 0 ? ts : null;
}

/** Build the composite map key used by resolvedChallenges and pendingResults. */
export function jankenMapKey(challengeId: string, userId: string): string {
  return `${challengeId}_${userId}`;
}

/**
 * Deterministically roll a reward for a janken challenge.
 * Uses a separate PRNG seed (suffixed with `-reward`) so the reward
 * is independent of the move derivation.
 *
 * @example
 * rollJankenReward('jnk_1708300000000_482917365', 'Uf4a3b2c1d0e9f8a7b6c5d4e3f2a1b0cd', 'U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b')
 * // => { type: 'boosters', subType: 'bullet', amount: 1 }
 */
export function rollJankenReward(
  challengeId: string,
  senderId: string,
  receiverId: string,
): Match2Reward {
  const random = createPrng(`${challengeId}-${senderId}-${receiverId}-reward`);
  const weights = jankenRewardTable.map((entry) => entry.weight);
  const index = weightedRandom(weights, random);
  return { ...jankenRewardTable[index].reward };
}
